import React, { useCallback, useContext } from 'react'
import moment from 'moment'
import { INTL_SHORT_DAY_NAME_DAY_MONTH } from 'constants/dateFormats'
import styled from 'styled-components'
import { SEVEN_DAYS_AFTER_PURCHASE, FOURTEEN_DAYS_BEFORE_CHECK_IN } from 'constants/cancellationPolicies'
import BodyText from 'LuxKit/Typography/BodyText'
import { getFlexibleCancellationDays, isCharterCancellationPolicy } from 'lib/offer/cancellationPolicyUtils'
import LineRefundIcon from 'LuxKit/Icons/line/LineRefundIcon'
import TextLink from 'LuxKit/TextLink'
import Clickable from 'components/Common/Clickable'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import ModalContext from 'contexts/ModalContext'
import BookingProtectionViaInfoModal from 'checkout/Components/CheckoutSteps/Review/BookingProtection/BookingProtectionViaInfoModal'
import { useIntlDateFormatter } from 'lib/datetime/dateUtils'
import { BOOKING_PROTECTION_ELIGABILITY_DAY_RANGE } from 'constants/bookingProtection'
import OfferPropertyFlexibleCancellationPolicyModal from './OfferPropertyFlexibleCancellationPolicyModal'
import OfferPropertyFlexibleCancellationCharterPolicyModal from './OfferPropertyFlexibleCancellationCharterPolicyModal'
import { useAppSelector } from 'hooks/reduxHooks'
import { isCharterEnabled } from 'selectors/featuresSelectors'

const MiddledTooltipIcon = styled(LineInfoCircleIcon)`
  vertical-align: middle;
`

interface Props {
  cancellationPolicy: App.OfferCancellationPolicy
  propertyTimezoneOffset?: number
  checkInDate?: string | moment.Moment
  isFlashUSP?: boolean
  isBookingProtectionEnabled?: boolean
  cartIncludesFlights?: boolean
}

function OfferPropertyFlexibleCancellationPolicies(props: Props) {
  const { cancellationPolicy, propertyTimezoneOffset, checkInDate, isFlashUSP, isBookingProtectionEnabled = false, cartIncludesFlights } = props
  const intlDateFormatter = useIntlDateFormatter()

  const days = getFlexibleCancellationDays(cancellationPolicy.type)
  let startBasedOnToday = propertyTimezoneOffset ? moment().utcOffset(propertyTimezoneOffset).add(SEVEN_DAYS_AFTER_PURCHASE, 'days') : moment().add(SEVEN_DAYS_AFTER_PURCHASE, 'days')
  let startBasedOnCheckIn = checkInDate && propertyTimezoneOffset ? moment(checkInDate).utcOffset(propertyTimezoneOffset).subtract(days, 'days') : moment(checkInDate).subtract(days, 'days')

  const changeOfMindDeadline = checkInDate && propertyTimezoneOffset ? moment(checkInDate).utcOffset(propertyTimezoneOffset).subtract(FOURTEEN_DAYS_BEFORE_CHECK_IN, 'days') : moment(checkInDate).subtract(FOURTEEN_DAYS_BEFORE_CHECK_IN, 'days')
  startBasedOnToday = startBasedOnToday.isBefore(changeOfMindDeadline) ? startBasedOnToday : changeOfMindDeadline

  const cancellationAfterChangeOfMindPeriod = !!(checkInDate && startBasedOnToday.isBefore(changeOfMindDeadline) && moment().add(SEVEN_DAYS_AFTER_PURCHASE, 'days').isBefore(moment(checkInDate).subtract(days, 'days')))
  startBasedOnCheckIn = cancellationAfterChangeOfMindPeriod ? startBasedOnCheckIn : startBasedOnToday

  const bookingProtectionLastEligibleRefundDate = checkInDate ? moment(checkInDate).add(BOOKING_PROTECTION_ELIGABILITY_DAY_RANGE, 'days') : undefined

  const isCharterModeEnabled = useAppSelector(isCharterEnabled)
  const isCharterPolicy = isCharterCancellationPolicy(cancellationPolicy)
  const charterPolicyApplies = isCharterPolicy && isCharterModeEnabled
  const showModal = useContext(ModalContext)

  const onItemClick = useCallback<React.MouseEventHandler<HTMLAnchorElement>>((e) => {
    e.preventDefault()
    if (charterPolicyApplies) {
      showModal(
        <OfferPropertyFlexibleCancellationCharterPolicyModal
          checkInDate={checkInDate}
          startBasedOnToday={startBasedOnToday}
          startBasedOnCheckIn={startBasedOnCheckIn}
          cancellationAfterChangeOfMindPeriod={cancellationAfterChangeOfMindPeriod}
          days={days}
        />,
      )
    } else if (isBookingProtectionEnabled && startBasedOnCheckIn && !cartIncludesFlights) {
      showModal(
        <BookingProtectionViaInfoModal
          cancellationType="flexible"
          cancelUntilFullRefundDate={startBasedOnToday}
          cancelAfterNonRefundableDate={startBasedOnCheckIn}
          bookingProtectionLastEligibleRefundDate={bookingProtectionLastEligibleRefundDate}
          cancellationAfterChangeOfMindPeriod={cancellationAfterChangeOfMindPeriod}
        />,
      )
    } else {
      showModal(
        <OfferPropertyFlexibleCancellationPolicyModal
          checkInDate={checkInDate}
          startBasedOnToday={startBasedOnToday}
          startBasedOnCheckIn={startBasedOnCheckIn}
          cancellationAfterChangeOfMindPeriod={cancellationAfterChangeOfMindPeriod}
          days={days}
        />,
      )
    }
  }, [
    isBookingProtectionEnabled,
    showModal,
    startBasedOnToday,
    startBasedOnCheckIn,
    bookingProtectionLastEligibleRefundDate,
    cancellationAfterChangeOfMindPeriod,
    cartIncludesFlights,
    checkInDate,
    days,
    charterPolicyApplies,
  ])

  return <>
    {!isFlashUSP &&
      <Clickable onClick={onItemClick}>
        <BodyText variant="medium" weight="bold" as="span" colour="highlight-secondary">Flexible cancellation</BodyText>{' '}
        {!checkInDate && <BodyText variant="medium" as="span">up to 7 days after purchase {!charterPolicyApplies ? '(Hotel only)' : ''}</BodyText>}
        {checkInDate && startBasedOnCheckIn && <BodyText variant="medium" as="span">until {intlDateFormatter(startBasedOnCheckIn, INTL_SHORT_DAY_NAME_DAY_MONTH)} {!charterPolicyApplies ? '(Hotel only)' : ''}</BodyText>}
        {' '}<MiddledTooltipIcon size="XS" />
      </Clickable>}
    {isFlashUSP &&
      <TextLink
        size="medium"
        colour="highlight-secondary"
        underline={false}
        data-type="7day"
        onClick={onItemClick}
        startIcon={<LineRefundIcon />}
      >
        Flexible cancellation
      </TextLink>}
  </>
}

export default OfferPropertyFlexibleCancellationPolicies
